import { defineMessages, useIntl } from 'react-intl';
import { Outlet } from 'react-router-dom-v5-compat';

import { Box } from '~/eds/src/components/Box/Box';
import { Grid } from '~/eds/src/components/Grid/Grid';
import { usePanelContext } from '~/eds/src/components/Panel/PanelContext';
import { Tabs } from '~/eds/src/components/Tabs/Tabs';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

import {
  TICKET_DETAILS_MAIN_CONTENT_MIN_WIDTH,
  TICKET_DETAILS_PANEL_WIDTH,
} from './constants';

const TAB_VALUES = ['tasks', 'documents', 'activity'] as const;
type TabValue = (typeof TAB_VALUES)[number];

const isValidTab = (segment: string): segment is TabValue =>
  TAB_VALUES.includes(segment as TabValue);

const messages = defineMessages({
  tasks: {
    id: 'TicketDetailsMain.tasksTab',
    defaultMessage: 'Tasks',
    description: 'Tasks tab label on Ticket Details page',
  },
  documents: {
    id: 'TicketDetailsMain.documentsTab',
    defaultMessage: 'Documents',
    description: 'Documents tab label on Ticket Details page',
  },
  activity: {
    id: 'TicketDetailsMain.activityTab',
    defaultMessage: 'Activity',
    description: 'Activity tab label on Ticket Details page',
  },
});

export const TicketDetailsMain = () => {
  const { navigate, params, location } = useRouting();
  const { ticketId } = params;

  const intl = useIntl();

  const { openPanelId, closePanel } = usePanelContext();
  const isPanelOpen = Boolean(openPanelId);

  const pathSegment = location.pathname.split('/').pop() ?? '';
  const selectedTab: TabValue = isValidTab(pathSegment) ? pathSegment : 'tasks';

  const basePath = RoutePathType.WorkflowCLMEvolutionTicketsDetail.replace(
    ':ticketId',
    ticketId!,
  );

  const handleSelectTab = (tab: TabValue) => {
    if (openPanelId) {
      closePanel(openPanelId);
    }
    navigate(`${basePath}/${tab}`, { replace: true });
  };

  const OutletComponent = <Outlet />;
  const tabs = [
    {
      value: 'tasks' as TabValue,
      label: intl.formatMessage(messages.tasks),
      panel: OutletComponent, // nested route components are defined in routes.ts
    },
    {
      value: 'documents' as TabValue,
      label: intl.formatMessage(messages.documents),
      panel: OutletComponent,
    },
    {
      value: 'activity' as TabValue,
      label: intl.formatMessage(messages.activity),
      panel: OutletComponent,
    },
  ];

  return (
    <Grid
      width="100%"
      columns={isPanelOpen ? 2 : 1}
      columnWidths={
        isPanelOpen
          ? ['minmax(0, 1fr)', `minmax(0, ${TICKET_DETAILS_PANEL_WIDTH})`]
          : ['minmax(0, 1fr)']
      }
    >
      <Box minW={TICKET_DETAILS_MAIN_CONTENT_MIN_WIDTH}>
        <Tabs
          selectedTab={selectedTab}
          tabs={tabs}
          onSelectTab={handleSelectTab}
          enableBorder
        />
      </Box>
    </Grid>
  );
};
